//github.com/visgl/react-google-maps/blob/main/examples/autocomplete/src/hooks/use-autocomplete-suggestions.ts

import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useRef, useState } from "react";
import { useDebounce } from "./use-debounce";

export type UsePlacesAutocompleteReturn = {
  suggestions: google.maps.places.AutocompleteSuggestion[];
  isLoading: boolean;
  resetSession: () => void;
};

/**
 * A reusable hook that retrieves autocomplete suggestions from the Google Places API.
 * The data is loaded from the new Autocomplete Data API.
 * (https://developers.google.com/maps/documentation/javascript/place-autocomplete-data)
 *
 * @param inputString The input string for which to fetch autocomplete suggestions.
 * @param requestOptions Additional options for the autocomplete request
 *   (See {@link https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#AutocompleteRequest}).
 * @param debounceMs The number of milliseconds to debounce the input (default: 300ms)
 *
 * @returns An object containing the autocomplete suggestions, the current loading-status,
 *   and a function to reset the session.
 *
 * @example
 * ```jsx
 * const MyComponent = () => {
 *   const [input, setInput] = useState('');
 *   const { suggestions, isLoading, resetSession } = useAutocompleteSuggestions(input, {
 *     includedPrimaryTypes: ['restaurant']
 *   });
 *
 *   return (
 *     <div>
 *       <input value={input} onChange={(e) => setInput(e.target.value)} />
 *       <ul>
 *         {suggestions.map(({placePrediction}) => (
 *           <li key={placePrediction.placeId}>{placePrediction.text.text}</li>
 *         ))}
 *       </ul>
 *     </div>
 *   );
 * }
 * ```
 */
export function usePlacesAutocomplete(
  inputString: string,
  requestOptions: Partial<google.maps.places.AutocompleteRequest> = {},
  debounceMs = 300,
): UsePlacesAutocompleteReturn {
  const placesLib = useMapsLibrary("places");
  const debouncedInput = useDebounce(inputString, debounceMs);

  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken>(null);

  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([]);

  const [isLoading, setIsLoading] = useState(false);

  // Create a stable dependency from the requestOptions object.
  const stringifiedRequestOptions = JSON.stringify(requestOptions);

  useEffect(() => {
    if (!placesLib) {
      return;
    }

    // This check is now simpler and has no dependencies.
    if (debouncedInput === "") {
      setSuggestions([]);
      return;
    }

    const { AutocompleteSessionToken, AutocompleteSuggestion } = placesLib;

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new AutocompleteSessionToken();
    }

    // We parse the stringified options back into an object inside the effect.
    const parsedRequestOptions = JSON.parse(stringifiedRequestOptions);

    const request: google.maps.places.AutocompleteRequest = {
      ...parsedRequestOptions,
      input: debouncedInput,
      sessionToken: sessionTokenRef.current,
    };

    setIsLoading(true);
    AutocompleteSuggestion.fetchAutocompleteSuggestions(request).then((res) => {
      setSuggestions(res.suggestions);
      setIsLoading(false);
    });
    // The dependency array is now correct and stable.
  }, [placesLib, debouncedInput, stringifiedRequestOptions]);

  return {
    suggestions,
    isLoading,
    resetSession: () => {
      sessionTokenRef.current = null;
      setSuggestions([]);
    },
  };
}
